import React, { useState } from 'react';
import { 
  Database, 
  Eye, 
  ChevronDown, 
  ChevronRight, 
  CheckCircle, 
  AlertCircle, 
  Info, 
  Layers, 
  Table, 
  GitBranch,
  TrendingUp,
  ArrowLeft,
  Download
} from 'lucide-react';
import { datasourcesApi } from '../services/datasources.service';

interface HefestoMeasure {
  name: string;
  type: 'SUM' | 'COUNT' | 'AVG' | 'MIN' | 'MAX';
  sourceColumn: string;
  description?: string;
}

interface HefestoDimension {
  name: string;
  role?: string;
  attributes: string[];
  joinKey?: string;
  description?: string;
}

interface HefestoRelationship {
  type: 'fact_to_dimension' | 'dimension_to_dimension';
  from: string;
  to: string;
  cardinality: '1:1' | '1:N' | 'N:1' | 'N:M';
  justification: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

interface HefestoFactTable {
  factName: string;
  measures: HefestoMeasure[];
  grain: string;
  grainExplanation: string;
}

interface HefestoModel {
  factTables: HefestoFactTable[];
  dimensions: HefestoDimension[];
  relationships: HefestoRelationship[];
  businessNarrative: string;
  warnings: string[];
  assumptions: string[];
  confidenceLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  generatedAt: string;
}

interface Props {
  model: HefestoModel;
  datasourceIds: string[];
  onBack: () => void;
  onModelSaved?: () => void;
}

const HefestoModelView: React.FC<Props> = ({ model, datasourceIds, onBack, onModelSaved }) => {
  const [showPreview, setShowPreview] = useState(true);
  const [expandedFact, setExpandedFact] = useState<number | null>(0);

  const getConfidenceData = (level: string) => {
    const config = {
      HIGH: { percentage: 85, color: 'bg-sky-500', bgClass: 'bg-sky-500/20', borderClass: 'border-sky-500/30', textClass: 'text-sky-400' },
      MEDIUM: { percentage: 55, color: 'bg-yellow-500', bgClass: 'bg-yellow-500/20', borderClass: 'border-yellow-500/30', textClass: 'text-yellow-400' },
      LOW: { percentage: 25, color: 'bg-red-500', bgClass: 'bg-red-500/20', borderClass: 'border-red-500/30', textClass: 'text-red-400' }
    };
    return config[level as keyof typeof config] || config.LOW;
  };

  const confidence = getConfidenceData(model.confidenceLevel);

  const handleExportJSON = () => {
    const dataStr = JSON.stringify(model, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `modelo-hefesto-${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={onBack}
            className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            VOLVER
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Modelo Analítico Hefesto
              </h1>
              <p className="text-slate-400 text-sm mt-2">
                Generado el {new Date(model.generatedAt).toLocaleString('es-AR')}
              </p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={handleExportJSON}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center gap-2 transition-all shadow-lg"
              >
                <Download className="w-4 h-4" />
                Exportar JSON
              </button>
            </div>
          </div>
        </div>

        {/* Confidence Card */}
        <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 p-6 mb-6 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Layers className="w-6 h-6 text-purple-400" />
              Confianza del Modelo
            </h2>
            <span className={`px-4 py-1.5 ${confidence.bgClass} ${confidence.textClass} rounded-full text-sm font-medium border ${confidence.borderClass}`}>
              {model.confidenceLevel}
            </span>
          </div>
          <div className="relative w-full h-3 bg-slate-700/50 rounded-full overflow-hidden">
            <div 
              className={`h-full ${confidence.color} transition-all duration-500 rounded-full`}
              style={{ width: `${confidence.percentage}%` }}
            />
          </div>
          <p className="text-slate-400 text-sm mt-2">{confidence.percentage}% de confianza</p>
        </div>

        {/* Business Narrative */}
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur rounded-2xl border border-blue-500/30 p-6 mb-6 shadow-xl">
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-blue-400" />
            Narrativa de Negocio
          </h3>
          <p className="text-slate-300">{model.businessNarrative}</p>
        </div>

        {/* Warnings */}
        {model.warnings.length > 0 && (
          <div className="bg-yellow-500/10 backdrop-blur rounded-2xl border border-yellow-500/30 p-6 mb-6 shadow-xl">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4 text-yellow-400">
              <AlertCircle className="w-5 h-5" />
              Advertencias
            </h3>
            <div className="space-y-2">
              {model.warnings.map((warning, idx) => (
                <div key={idx} className="flex items-start gap-2 text-yellow-200">
                  <span className="text-yellow-400 mt-1">▲</span>
                  <p>{warning}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Assumptions */}
        {model.assumptions.length > 0 && (
          <div className="bg-blue-500/10 backdrop-blur rounded-2xl border border-blue-500/30 p-6 mb-6 shadow-xl">
            <h3 className="text-lg font-semibold flex items-center gap-2 mb-4 text-blue-400">
              <Info className="w-5 h-5" />
              Supuestos del Modelo
            </h3>
            <div className="space-y-2">
              {model.assumptions.map((assumption, idx) => (
                <div key={idx} className="flex items-start gap-2 text-blue-200">
                  <span className="text-blue-400 mt-1">•</span>
                  <p>{assumption}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Preview Section */}
        <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 p-6 mb-6 shadow-2xl">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="w-full flex items-center justify-between mb-4"
          >
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Eye className="w-6 h-6 text-purple-400" />
              Vista Previa del Modelo Estrella
            </h3>
            {showPreview ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>

          {showPreview && (
            <div className="space-y-6 mt-6">
              {/* Fact Tables */}
              {model.factTables.map((fact, idx) => (
                <div key={idx}>
                  <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Table className="w-5 h-5 text-yellow-400" />
                        <h4 className="text-lg font-bold text-yellow-300">{fact.factName}</h4>
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">Tabla de Hechos</span>
                      </div>
                      <button 
                        onClick={() => setExpandedFact(expandedFact === idx ? null : idx)}
                        className="text-slate-400 hover:text-white transition-colors"
                      >
                        {expandedFact === idx ? 
                          <ChevronDown className="w-5 h-5" /> : 
                          <ChevronRight className="w-5 h-5" />
                        }
                      </button>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm text-slate-400">Granularidad:</p>
                      <p className="text-yellow-200">{fact.grain}</p>
                      <p className="text-xs text-slate-400 italic mt-1">{fact.grainExplanation}</p>
                    </div>

                    {expandedFact === idx && (
                      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-yellow-500/20">
                        <div>
                          <p className="text-sm text-slate-400 mb-2">Métricas:</p>
                          {fact.measures.map((measure, midx) => (
                            <div key={midx} className="text-sm text-yellow-200 mb-2">
                              <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                <span className="font-medium">{measure.name}</span>
                                <span className="text-xs text-yellow-400">({measure.type})</span>
                              </div>
                              {measure.description && (
                                <p className="text-xs text-slate-400 ml-6">{measure.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Dimensions */}
              <div className="grid grid-cols-2 gap-4">
                {model.dimensions.map((dim, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl p-4 border border-blue-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <GitBranch className="w-4 h-4 text-blue-400" />
                      <h4 className="font-semibold text-blue-300">
                        {dim.name === 'tiempo' ? '📅' : dim.name === 'cliente' ? '👤' : '📦'} {dim.name}
                      </h4>
                      {dim.role && (
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">{dim.role}</span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {dim.attributes.map((attr, i) => (
                        <div key={i} className="text-sm text-slate-300">• {attr}</div>
                      ))}
                    </div>
                    {dim.joinKey && (
                      <p className="text-xs text-slate-500 mt-2">🔑 {dim.joinKey}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Relationships */}
              {model.relationships.length > 0 && (
                <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-600">
                  <h4 className="text-lg font-semibold mb-4 text-purple-300 flex items-center gap-2">
                    <GitBranch className="w-5 h-5" />
                    Relaciones del Modelo
                  </h4>
                  <div className="space-y-3">
                    {model.relationships.map((rel, idx) => (
                      <div key={idx} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded text-sm">{rel.from}</span>
                          <span className="text-slate-400">→</span>
                          <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded text-sm">{rel.to}</span>
                          <span className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-xs">{rel.cardinality}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            rel.confidence === 'HIGH' ? 'bg-sky-500/20 text-sky-400' :
                            rel.confidence === 'MEDIUM' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {rel.confidence}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400">{rel.justification}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Star Schema Visualization */}
              {model.factTables.length > 0 && model.dimensions.length > 0 && (
                <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-600">
                  <h4 className="text-center text-lg font-semibold mb-6 text-purple-300">Esquema Estrella</h4>
                  <div className="flex items-center justify-center">
                    <svg viewBox="0 0 600 400" className="w-full max-w-2xl">
                      {/* Center Fact Table */}
                      <rect x="225" y="150" width="150" height="100" fill="#fbbf24" opacity="0.2" stroke="#fbbf24" strokeWidth="2" rx="8"/>
                      <text x="300" y="190" textAnchor="middle" fill="#fbbf24" fontSize="14" fontWeight="bold">
                        {model.factTables[0]?.factName || 'Hechos'}
                      </text>
                      <text x="300" y="210" textAnchor="middle" fill="#fcd34d" fontSize="10">📊 Métricas</text>
                      <text x="300" y="225" textAnchor="middle" fill="#fcd34d" fontSize="10">🔑 Claves FK</text>
                      
                      {/* Dimensions - positioned around the fact table */}
                      {model.dimensions.slice(0, 4).map((dim, idx) => {
                        const positions = [
                          { x: 225, y: 20, lineX1: 300, lineY1: 100, lineX2: 300, lineY2: 150 }, // Top
                          { x: 450, y: 160, lineX1: 375, lineY1: 200, lineX2: 450, lineY2: 200 }, // Right
                          { x: 225, y: 300, lineX1: 300, lineY1: 250, lineX2: 300, lineY2: 300 }, // Bottom
                          { x: 20, y: 160, lineX1: 150, lineY1: 200, lineX2: 225, lineY2: 200 } // Left
                        ];
                        const pos = positions[idx];
                        return (
                          <g key={idx}>
                            <rect x={pos.x} y={pos.y} width={idx === 1 || idx === 3 ? 130 : 150} height="80" 
                              fill="#3b82f6" opacity="0.2" stroke="#3b82f6" strokeWidth="2" rx="8"/>
                            <text x={pos.x + (idx === 1 || idx === 3 ? 65 : 75)} y={pos.y + 30} 
                              textAnchor="middle" fill="#3b82f6" fontSize="12" fontWeight="bold">
                              {dim.name}
                            </text>
                            <line x1={pos.lineX1} y1={pos.lineY1} x2={pos.lineX2} y2={pos.lineY2} 
                              stroke="#60a5fa" strokeWidth="2"/>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="bg-blue-500/5 backdrop-blur rounded-xl border border-blue-500/20 p-4">
          <p className="text-sm text-slate-400 flex items-center gap-2">
            <Info className="w-4 h-4 text-blue-400" />
            <span>
              Modelo generado automáticamente usando metodología Hefesto. 
              Las tablas de dimensiones y hechos se infieren de los metadatos semánticos de las fuentes seleccionadas.
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default HefestoModelView;
